import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";

function tagsToString(tags) {
  if (Array.isArray(tags)) return tags.join(", ");
  return String(tags || "");
}

function GroundingEditDialog({
  open,
  onClose,
  onSubmit,
  onDelete,
  initial,
  submitLabel = "Gem",
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setTags(tagsToString(initial?.tags));
    setBody(initial?.body || "");
    setBusy(false);
    setError("");
  }, [open, initial]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Titel er påkrævet.");
      return;
    }
    if (!body.trim()) {
      setError("Indhold er påkrævet.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSubmit?.({
        title: title.trim(),
        body,
        tags,
        composition: "grounding",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke gemme.");
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setBusy(true);
    setError("");
    try {
      await onDelete();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke slette.");
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          height: fullScreen ? "100%" : "min(90vh, 860px)",
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Rediger refleksion-journal
        <IconButton
          aria-label="Luk"
          onClick={onClose}
          disabled={busy}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          disabled={busy}
        />
        <TextField
          label="Tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          fullWidth
          disabled={busy}
          helperText="Kommasepareret"
        />
        <Box data-color-mode={theme.palette.mode}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Indhold
          </Typography>
          <MDEditor value={body} onChange={(val) => setBody(val || "")} height={360} preview="live" />
        </Box>
        {error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Box>
          {onDelete ? (
            <Button color="error" onClick={handleDelete} disabled={busy}>
              Slet
            </Button>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={busy}>
            Annuller
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={busy}>
            {busy ? "Gemmer..." : submitLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default GroundingEditDialog;
